import { Box, Typography, Stack, Avatar } from "@mui/joy";
import { PhoneRounded, VideocamRounded, DescriptionRounded, AttachMoneyRounded, AttachMoneyOutlined, MonetizationOn } from "@mui/icons-material";
import TotalApplicants from "../../assets/svgs/totalapplicants.svg";

interface ApplicantItemProps {
  fullName: string;
  date: string;
  status: "Published" | "Unpublished";
  isLast?: boolean;
  applicantUuid?: string | number;
  onApplicantClick: () => void;
  // Attribute indicators
  hasVerifiedPhone: boolean;
  hasVideo: boolean;
  hasResume: boolean;
  hasSuperApplied: boolean;
  rating: number | null;
}

const ApplicantItem = ({
  fullName,
  date,
  status,
  isLast,
  onApplicantClick,
  hasVerifiedPhone,
  hasVideo,
  hasResume,
  hasSuperApplied,
  rating,
}: ApplicantItemProps) => {
  const isPublished = status === "Published";

  return (
    <Box
      px={2}
      py={1.5}
      sx={{
        borderBottom: isLast ? "none" : "1px solid #e0e0e0",
        cursor: "pointer"
      }}
      onClick={onApplicantClick}
    >
      {/* Desktop Layout */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        {/* Left side: Avatar and Name */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
          <Avatar src={TotalApplicants} />
          <Typography level="description" sx={{ color: "#1A1B1D" }}>
            {fullName}
          </Typography>
        </Stack>

        {/* Right side: Icons and Date/Status */}
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Attribute Indicators */}
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Phone Icon */}
            <PhoneRounded 
              sx={{ 
                fontSize: 20, 
                color: hasVerifiedPhone ? "#1A1B1D" : "#E5E7EB" 
              }} 
            />
            
            {/* Camera/Video Icon */}
            <VideocamRounded 
              sx={{ 
                fontSize: 20, 
                color: hasVideo ? "#1A1B1D" : "#E5E7EB" 
              }} 
            />
            
            {/* Document/Resume Icon */}
            <DescriptionRounded 
              sx={{ 
                fontSize: 20, 
                color: hasResume ? "#1A1B1D" : "#E5E7EB" 
              }} 
            />
            
            {/* Dollar Sign for Super Applied */}
            <MonetizationOn
              sx={{ 
                fontSize: 20, 
                color: hasSuperApplied ? "#1A1B1D" : "#E5E7EB" 
              }} 
            />
            
            {/* Rating Number */}
            <Typography 
              level="body-sm" 
              sx={{ 
                fontSize: 16,
                fontWeight: 600,
                color: rating !== null ? "#1A1B1D" : "#E5E7EB",
                minWidth: 28,
                textAlign: "center"
              }}
            >
              {rating !== null ? rating.toFixed(1) : "0.0"}
            </Typography>
          </Stack>

          {/* Date and Status */}
          <Stack alignItems="flex-end" spacing={0.5} sx={{ minWidth: 80 }}>
            <Typography level="description">{date}</Typography>
            <Typography
              level="description"
              sx={{ fontSize: "13px", color: isPublished ? "#34C759" : "#D58238" }}
            >
              {status}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Mobile Layout */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        {/* First Row: Avatar and Name */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Avatar src={TotalApplicants} />
          <Typography level="description" sx={{ color: "#1A1B1D" }}>
            {fullName}
          </Typography>
        </Stack>

        {/* Second Row: Icons and Date/Status */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {/* Attribute Indicators */}
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Phone Icon */}
            <PhoneRounded 
              sx={{ 
                fontSize: 20, 
                color: hasVerifiedPhone ? "#1A1B1D" : "#E5E7EB" 
              }} 
            />
            
            {/* Camera/Video Icon */}
            <VideocamRounded 
              sx={{ 
                fontSize: 20, 
                color: hasVideo ? "#1A1B1D" : "#E5E7EB" 
              }} 
            />
            
            {/* Document/Resume Icon */}
            <DescriptionRounded 
              sx={{ 
                fontSize: 20, 
                color: hasResume ? "#1A1B1D" : "#E5E7EB" 
              }} 
            />
            
            {/* Dollar Sign for Super Applied */}
            <MonetizationOn 
              sx={{ 
                fontSize: 20, 
                color: hasSuperApplied ? "#1A1B1D" : "#E5E7EB" 
              }} 
            />
            
            {/* Rating Number */}
            <Typography 
              level="body-sm" 
              sx={{ 
                fontSize: 16,
                fontWeight: 600,
                color: rating !== null ? "#1A1B1D" : "#E5E7EB",
                minWidth: 28,
                textAlign: "center"
              }}
            >
              {rating !== null ? rating.toFixed(1) : "0.0"}
            </Typography>
          </Stack>

          {/* Date and Status */}
          <Stack alignItems="flex-end" spacing={0.5}>
            <Typography level="description" sx={{ fontSize: "12px" }}>{date}</Typography>
            <Typography
              level="description"
              sx={{ fontSize: "11px", color: isPublished ? "#34C759" : "#D58238" }}
            >
              {status}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default ApplicantItem;
